/**
 * pages.config.js - Page routing configuration
 *
 * This file is AUTO-GENERATED. Do not add imports or modify PAGES manually.
 * Pages are auto-registered when you create files in the ./pages/ folder.
 *
 * THE ONLY EDITABLE VALUE: mainPage
 * This controls which page is the landing page (shown when users visit the app).
 *
 * Example file structure:
 *
 *   import HomePage from './pages/HomePage';
 *   import Dashboard from './pages/Dashboard';
 *   import Settings from './pages/Settings';
 *
 *   export const PAGES = {
 *       "HomePage": HomePage,
 *       "Dashboard": Dashboard,
 *       "Settings": Settings,
 *   }
 *
 *   export const pagesConfig = {
 *       mainPage: "HomePage",
 *       Pages: PAGES,
 *   };
 *
 * Example with Layout (wraps all pages):
 *
 *   import Home from './pages/Home';
 *   import Settings from './pages/Settings';
 *   import __Layout from './Layout.jsx';
 *
 *   export const PAGES = {
 *       "Home": Home,
 *       "Settings": Settings,
 *   }
 *
 *   export const pagesConfig = {
 *       mainPage: "Home",
 *       Pages: PAGES,
 *       Layout: __Layout,
 *   };
 *
 * To change the main page from HomePage to Dashboard, use find_replace:
 *   Old: mainPage: "HomePage",
 *   New: mainPage: "Dashboard",
 *
 * The mainPage value must match a key in the PAGES object exactly.
 */
import { lazy } from 'react';
import __Layout from './Layout.jsx';

const About = lazy(() => import('./pages/About'));
const CategoryAreaLanding = lazy(() => import('./pages/CategoryAreaLanding'));
const TourismGuide = lazy(() => import('./pages/TourismGuide'));
const AccountActivation = lazy(() => import('./pages/AccountActivation'));
const AddBusiness = lazy(() => import('./pages/AddBusiness'));
const AdminAnalytics = lazy(() => import('./pages/AdminAnalytics'));
const AdminFeedback = lazy(() => import('./pages/AdminFeedback'));
const AdminImport = lazy(() => import('./pages/AdminImport'));
const AdminAuditLog = lazy(() => import('./pages/AdminAuditLog'));
const AdminBlog = lazy(() => import('./pages/AdminBlog'));
const AdminBookings = lazy(() => import('./pages/AdminBookings'));
const AdminBusinesses = lazy(() => import('./pages/AdminBusinesses'));
const AdminCampaigns = lazy(() => import('./pages/AdminCampaigns'));
const AdminCategories = lazy(() => import('./pages/AdminCategories'));
const AdminDashboard = lazy(() => import('./pages/AdminDashboard'));
const AdminDeals = lazy(() => import('./pages/AdminDeals'));
const AdminExperiences = lazy(() => import('./pages/AdminExperiences'));
const AdminEvents = lazy(() => import('./pages/AdminEvents'));
const AdminReports = lazy(() => import('./pages/AdminReports'));
const AdminReviews = lazy(() => import('./pages/AdminReviews'));
const AdminUsers = lazy(() => import('./pages/AdminUsers'));
const AdminVerifications = lazy(() => import('./pages/AdminVerifications'));
const AreaGuide = lazy(() => import('./pages/AreaGuide'));
const AvailabilityManagement = lazy(() => import('./pages/AvailabilityManagement'));
const Blog = lazy(() => import('./pages/Blog'));
const BlogPost = lazy(() => import('./pages/BlogPost'));
const BookingAnalytics = lazy(() => import('./pages/BookingAnalytics'));
const BookingForm = lazy(() => import('./pages/BookingForm'));
const BusinessBookingSetup = lazy(() => import('./pages/BusinessBookingSetup'));
const BusinessDetails = lazy(() => import('./pages/BusinessDetails'));
const BusinessOnboarding = lazy(() => import('./pages/BusinessOnboarding'));
const Cart = lazy(() => import('./pages/Cart'));
const Checkout = lazy(() => import('./pages/Checkout'));
const ClaimBusiness = lazy(() => import('./pages/ClaimBusiness'));
const Contact = lazy(() => import('./pages/Contact'));
const Deals = lazy(() => import('./pages/Deals'));
const DealDetails = lazy(() => import('./pages/DealDetails'));
const ReviewFlyer = lazy(() => import('./pages/ReviewFlyer'));
const Documentation = lazy(() => import('./pages/Documentation'));
const EditBusiness = lazy(() => import('./pages/EditBusiness'));
const EventDetails = lazy(() => import('./pages/EventDetails'));
const Events = lazy(() => import('./pages/Events'));
const Explore = lazy(() => import('./pages/Explore'));
const FAQ = lazy(() => import('./pages/FAQ'));
const FollowingBusinesses = lazy(() => import('./pages/FollowingBusinesses'));
const Home = lazy(() => import('./pages/Home'));
const ManageBookings = lazy(() => import('./pages/ManageBookings'));
const ManageDeals = lazy(() => import('./pages/ManageDeals'));
const ManageExperiences = lazy(() => import('./pages/ManageExperiences'));
const ManageEnquiries = lazy(() => import('./pages/ManageEnquiries'));
const ManageEvents = lazy(() => import('./pages/ManageEvents'));
const ManageGallery = lazy(() => import('./pages/ManageGallery'));
const ManageMenu = lazy(() => import('./pages/ManageMenu'));
const ManageOrders = lazy(() => import('./pages/ManageOrders'));
const MyBusinesses = lazy(() => import('./pages/MyBusinesses'));
const ManageReviews = lazy(() => import('./pages/ManageReviews'));
const MyBookings = lazy(() => import('./pages/MyBookings'));
const MyOrders = lazy(() => import('./pages/MyOrders'));
const MyTickets = lazy(() => import('./pages/MyTickets'));
const MyClaims = lazy(() => import('./pages/MyClaims'));
const MyCalendar = lazy(() => import('./pages/MyCalendar'));
const MyReviews = lazy(() => import('./pages/MyReviews'));
const MyEnquiries = lazy(() => import('./pages/MyEnquiries'));
const MyDeals     = lazy(() => import('./pages/MyDeals'));
const Notifications = lazy(() => import('./pages/Notifications'));
const OrderConfirmation = lazy(() => import('./pages/OrderConfirmation'));
const OwnerAnalytics = lazy(() => import('./pages/OwnerAnalytics'));
const OwnerDashboard = lazy(() => import('./pages/OwnerDashboard'));
const OwnerSettings = lazy(() => import('./pages/OwnerSettings'));
const OwnerVerification = lazy(() => import('./pages/OwnerVerification'));
const PrivacyChoices = lazy(() => import('./pages/PrivacyChoices'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const ProfileSettings = lazy(() => import('./pages/ProfileSettings'));
const RealTimeNotificationsSetup = lazy(() => import('./pages/RealTimeNotificationsSetup'));
const Recommendations = lazy(() => import('./pages/Recommendations'));
const SavedBusinesses = lazy(() => import('./pages/SavedBusinesses'));
const SavedDeals = lazy(() => import('./pages/SavedDeals'));
const TeamMembers = lazy(() => import('./pages/TeamMembers'));
const TermsOfService = lazy(() => import('./pages/TermsOfService'));
const UserDashboard = lazy(() => import('./pages/UserDashboard'));
const UserGuide = lazy(() => import('./pages/UserGuide'));
const Login = lazy(() => import('./pages/Login'));
const Signup = lazy(() => import('./pages/Signup'));
const ContentGuidelines = lazy(() => import('./pages/ContentGuidelines'));
const CookiePolicy = lazy(() => import('./pages/CookiePolicy'));
const AccessibilityStatement = lazy(() => import('./pages/AccessibilityStatement'));
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'));
const ResetPassword = lazy(() => import('./pages/ResetPassword'));
const TicketVerify = lazy(() => import('./pages/TicketVerify'));
const JustArrived = lazy(() => import('./pages/JustArrived'));


export const PAGES = {
    "About": About,
    "CategoryAreaLanding": CategoryAreaLanding,
    "TourismGuide": TourismGuide,
    "AccountActivation": AccountActivation,
    "AddBusiness": AddBusiness,
    "AdminAnalytics": AdminAnalytics,
    "AdminFeedback": AdminFeedback,
    "AdminBlog": AdminBlog,
    "AdminBookings": AdminBookings,
    "AdminBusinesses": AdminBusinesses,
    "AdminCampaigns": AdminCampaigns,
    "AdminImport": AdminImport,
    "AdminAuditLog": AdminAuditLog,
    "AdminCategories": AdminCategories,
    "AdminDashboard": AdminDashboard,
    "AdminDeals": AdminDeals,
    "AdminExperiences": AdminExperiences,
    "AdminEvents": AdminEvents,
    "AdminReports": AdminReports,
    "AdminReviews": AdminReviews,
    "AdminUsers": AdminUsers,
    "AdminVerifications": AdminVerifications,
    "AreaGuide": AreaGuide,
    "AvailabilityManagement": AvailabilityManagement,
    "Blog": Blog,
    "BlogPost": BlogPost,
    "BookingAnalytics": BookingAnalytics,
    "BookingForm": BookingForm,
    "BusinessBookingSetup": BusinessBookingSetup,
    "BusinessDetails": BusinessDetails,
    "BusinessOnboarding": BusinessOnboarding,
    "Cart": Cart,
    "Checkout": Checkout,
    "ClaimBusiness": ClaimBusiness,
    "Contact": Contact,
    "Deals": Deals,
    "DealDetails": DealDetails,
    "ReviewFlyer": ReviewFlyer,
    "Documentation": Documentation,
    "EditBusiness": EditBusiness,
    "EventDetails": EventDetails,
    "Events": Events,
    "Explore": Explore,
    "FAQ": FAQ,
    "FollowingBusinesses": FollowingBusinesses,
    "Home": Home,
    "ManageBookings": ManageBookings,
    "ManageDeals": ManageDeals,
    "ManageExperiences": ManageExperiences,
    "ManageEnquiries": ManageEnquiries,
    "ManageEvents": ManageEvents,
    "ManageGallery": ManageGallery,
    "ManageMenu": ManageMenu,
    "ManageOrders": ManageOrders,
    "ManageReviews": ManageReviews,
    "MyBusinesses": MyBusinesses,
    "MyBookings": MyBookings,
    "MyOrders": MyOrders,
    "MyTickets": MyTickets,
    "MyClaims": MyClaims,
    "MyCalendar": MyCalendar,
    "MyReviews": MyReviews,
    "MyEnquiries": MyEnquiries,
    "MyDeals":     MyDeals,
    "Notifications": Notifications,
    "OrderConfirmation": OrderConfirmation,
    "OwnerAnalytics": OwnerAnalytics,
    "OwnerDashboard": OwnerDashboard,
    "OwnerSettings": OwnerSettings,
    "OwnerVerification": OwnerVerification,
    "PrivacyChoices": PrivacyChoices,
    "PrivacyPolicy": PrivacyPolicy,
    "ProfileSettings": ProfileSettings,
    "RealTimeNotificationsSetup": RealTimeNotificationsSetup,
    "Recommendations": Recommendations,
    "SavedBusinesses": SavedBusinesses,
    "SavedDeals": SavedDeals,
    "TeamMembers": TeamMembers,
    "TermsOfService": TermsOfService,
    "UserDashboard": UserDashboard,
    "UserGuide": UserGuide,
    "Login": Login,
    "Signup": Signup,
    "ContentGuidelines": ContentGuidelines,
    "CookiePolicy": CookiePolicy,
    "AccessibilityStatement": AccessibilityStatement,
    "ForgotPassword": ForgotPassword,
    "ResetPassword": ResetPassword,
    "TicketVerify": TicketVerify,
    "JustArrived": JustArrived,
}

export const pagesConfig = {
    mainPage: "Home",
    Pages: PAGES,
    Layout: __Layout,
};
